// §11.8 — "text search", as a pure function over the parsed document.
//
// A match is found **within one show-operation**, which is the unit the file itself puts text in.
// A phrase split across two operations by the producer's kerning is not found — the alternative,
// stitching runs into a synthetic line and mapping offsets back, guesses at word boundaries the
// file never stated and would report matches at positions that are not where the words are.
import type { PdfDocument } from './document';

export interface DocMatch {
  /** Zero-based. */
  readonly page: number;
  readonly runIndex: number;
  readonly start: number;
  readonly length: number;
  /** Zero-based position in this result set — what "3 of 11" counts. */
  readonly ordinal: number;
}

export function findMatches(doc: PdfDocument, query: string): DocMatch[] {
  const needle = query.trim().toLowerCase();
  if (needle.length === 0) return [];

  const out: DocMatch[] = [];
  doc.pages.forEach((page, pageIndex) => {
    page.runs.forEach((run, runIndex) => {
      const haystack = run.text.toLowerCase();
      let from = 0;
      for (;;) {
        const found = haystack.indexOf(needle, from);
        if (found < 0) break;
        out.push({
          page: pageIndex,
          runIndex,
          start: found,
          length: needle.length,
          ordinal: out.length,
        });
        from = found + needle.length;
      }
    });
  });
  return out;
}

/** The first match at or after `page`, so Enter from page 4 does not jump backwards to page 1. */
export function matchNearPage(matches: readonly DocMatch[], page: number): number {
  if (matches.length === 0) return -1;
  const found = matches.findIndex((match) => match.page >= page);
  return found < 0 ? 0 : found;
}
